import { useState, useEffect } from 'react'
import axios from 'axios'
import { Send } from 'lucide-react'
import { buildApiUrl } from '../config/api'
import { toast } from './Toast'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Select } from './ui'

interface AccessProfile {
  id: number
  code: string
  label: string
}

interface InviteUserModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  defaultName?: string
  defaultEmail?: string
  fixedProfileId?: number
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

export default function InviteUserModal({ open, onClose, onSuccess, defaultName, defaultEmail, fixedProfileId }: InviteUserModalProps) {
  const [profiles, setProfiles] = useState<AccessProfile[]>([])
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', access_profile_id: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setForm({
        name: defaultName || '',
        email: defaultEmail || '',
        access_profile_id: fixedProfileId ? String(fixedProfileId) : '',
      })
      setErrors({})
      if (profiles.length === 0) {
        axios.get(buildApiUrl('/v1/organization-users/access-profiles'))
          .then(r => setProfiles(r.data.data))
          .catch(() => {})
      }
    }
  }, [open, defaultName, defaultEmail, fixedProfileId])

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Nome é obrigatório'
    if (!form.email.trim()) e.email = 'E-mail é obrigatório'
    else if (!isValidEmail(form.email)) e.email = 'E-mail inválido'
    if (!form.access_profile_id) e.access_profile_id = 'Selecione o perfil'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleInvite() {
    if (saving) return
    if (!validate()) return
    setSaving(true)
    try {
      await axios.post(buildApiUrl('/v1/organization-users/invite'), {
        ...form,
        access_profile_id: parseInt(form.access_profile_id),
      })
      toast('success', 'Convite enviado')
      onClose()
      onSuccess?.()
    } catch (err: any) {
      toast('error', err.response?.data?.error || 'Erro ao enviar convite')
    } finally { setSaving(false) }
  }

  const profileOptions = profiles
    .filter(p => !['SYS_ADMIN'].includes(p.code))
    .map(p => ({ value: String(p.id), label: p.label }))

  const fixedProfileLabel = fixedProfileId ? profiles.find(p => p.id === fixedProfileId)?.label : null

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <ModalHeader onClose={onClose}>Convidar usuário</ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          <Input
            label="Nome"
            value={form.name}
            onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setErrors(e2 => ({ ...e2, name: '' })) }}
            placeholder="Nome do convidado"
            error={errors.name}
          />
          <Input
            label="E-mail"
            type="email"
            value={form.email}
            onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setErrors(e2 => ({ ...e2, email: '' })) }}
            placeholder="email@exemplo.com"
            error={errors.email}
          />
          {fixedProfileId ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Perfil de acesso</label>
              <div className="h-11 w-full rounded-lg border border-gray-200 px-4 flex items-center text-sm bg-gray-50 text-gray-600">
                {fixedProfileLabel || 'Carregando...'}
              </div>
            </div>
          ) : (
            <Select
              label="Perfil de acesso"
              value={form.access_profile_id}
              onChange={e => { setForm(f => ({ ...f, access_profile_id: e.target.value })); setErrors(e2 => ({ ...e2, access_profile_id: '' })) }}
              options={profileOptions}
              placeholder="Selecione o perfil"
              error={errors.access_profile_id}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={handleInvite} loading={saving}>
          <Send className="w-4 h-4 mr-1.5" />Enviar convite
        </Button>
      </ModalFooter>
    </Modal>
  )
}
